import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, QrCode, Users, Sparkles, Gift, Tag, 
  UserCog, BarChart3, Bell, LogOut, Menu, X, ChevronRight, Key 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ChangePasswordModal from '../components/ChangePasswordModal';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/scanner', icon: QrCode, label: 'QR Scanner' },
    { path: '/admin/customers', icon: Users, label: 'Customers' },
    { path: '/admin/services', icon: Sparkles, label: 'Services' },
    { path: '/admin/rewards', icon: Gift, label: 'Rewards' },
    { path: '/admin/promotions', icon: Tag, label: 'Promotions' },
    { path: '/admin/staff', icon: UserCog, label: 'Staff' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFFF0] flex">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#4A154B] text-white"
        data-testid="admin-mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 gradient-plum transform transition-all duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${collapsed ? 'w-20' : 'w-64'}
      `}>
        <div className="flex flex-col h-full p-4">
          {/* Logo */}
          <div className={`mb-8 ${collapsed ? 'text-center' : 'px-2'}`}>
            <h1 className={`font-serif text-white ${collapsed ? 'text-xl' : 'text-2xl'}`}>
              {collapsed ? 'SO' : 'Salon O'}
            </h1>
            {!collapsed && (
              <p className="text-xs text-[#C9A689] tracking-widest uppercase">Admin Portal</p>
            )}
          </div>

          {/* Collapse toggle - desktop only */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/70 hover:bg-white/20 mb-6 mx-auto"
          >
            <ChevronRight className={`transform transition-transform ${collapsed ? '' : 'rotate-180'}`} size={16} />
          </button>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto">
            {navItems.map(({ path, icon: Icon, label }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  sidebar-item ${isActive ? 'active' : ''}
                  ${collapsed ? 'justify-center px-2' : ''}
                `}
                title={collapsed ? label : undefined}
                data-testid={`admin-nav-${label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} strokeWidth={1.5} />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* User info & Logout */}
          <div className="border-t border-white/20 pt-4 mt-4">
            {!collapsed && (
              <div className="mb-4 px-2">
                <p className="text-white/60 text-sm">Logged in as</p>
                <p className="text-white font-medium truncate">{user?.name || 'Admin'}</p>
                <p className="text-[#C9A689] text-sm capitalize">{user?.role}</p>
              </div>
            )}
            <button
              onClick={() => setShowPasswordModal(true)}
              className={`sidebar-item w-full text-white/70 hover:text-white hover:bg-white/10 ${collapsed ? 'justify-center px-2' : ''}`}
              title={collapsed ? 'Change Password' : undefined}
            >
              <Key size={20} strokeWidth={1.5} />
              {!collapsed && <span>Change Password</span>}
            </button>
            <button
              onClick={handleLogout}
              className={`sidebar-item w-full text-red-300 hover:text-red-200 hover:bg-red-500/20 ${collapsed ? 'justify-center px-2' : ''}`}
              title={collapsed ? 'Logout' : undefined}
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} strokeWidth={1.5} />
              {!collapsed && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 min-h-screen overflow-x-hidden">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      <ChangePasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
    </div>
  );
};

export default AdminLayout;
